import moment from 'moment';

/**
 * Presence History Utility Functions
 * 
 * Provides helper functions for calculating and displaying
 * device connectivity presence data for a selected date range.
 */

/**
 * Connection type constants
 */
export const CONNECTION_TYPES = {
  WIFI: 'WIFI',
  BLE: 'BLE',
  BOTH: 'BOTH', // Multiple connection types on same day
  NONE: 'NONE',
};

/**
 * IAM identifier prefixes that indicate BLE connection (AWS IAM credentials)
 * @see https://docs.aws.amazon.com/IAM/latest/UserGuide/reference_identifiers.html#identifiers-prefixes
 */
const IAM_PREFIXES = [
  'AIDA', // IAM user
  'AROA', // IAM role
  'ASIA', // STS temporary credentials
  'ABIA', // AWS STS service bearer token
  'ACCA', // Context-specific credential
  'AGPA', // IAM group
  'AIPA', // EC2 instance profile
  'AKIA', // IAM access key
  'ANPA', // IAM managed policy
  'ANVA', // IAM managed policy version
  'APKA', // IAM public key
  'ASCA', // IAM certificate
];

/**
 * Determines the connection type based on principalID
 * @param {string} principalId - The principal ID from the connectivity event
 * @returns {string} CONNECTION_TYPES.WIFI, CONNECTION_TYPES.BLE, or null
 * 
 * @see https://docs.aws.amazon.com/iot/latest/developerguide/life-cycle-events.html
 * @see https://docs.aws.amazon.com/iot/latest/apireference/API_DescribeCertificate.html
 * @see https://docs.aws.amazon.com/IAM/latest/UserGuide/reference_identifiers.html#identifiers-prefixes
 * 
 * WIFI connections use certificate IDs: 64-character hex strings
 * BLE connections use IAM credentials: Start with IAM prefixes (AIDA, AROA, ASIA, etc.)
 */
export const getConnectionTypeFromPrincipalID = (principalId) => {
  if (!principalId) return null;
  
  // Check for IAM credential prefixes (BLE connection)
  const upperPrincipalId = principalId.toUpperCase();
  const hasIamPrefix = IAM_PREFIXES.some((prefix) => upperPrincipalId.startsWith(prefix));
  if (hasIamPrefix) {
    return CONNECTION_TYPES.BLE;
  }
  
  // Certificate IDs are 64-character hex strings (WIFI connection)
  // They consist only of hexadecimal characters (0-9, a-f)
  const hexPattern = /^[a-f0-9]{64}$/i;
  if (hexPattern.test(principalId)) {
    return CONNECTION_TYPES.WIFI;
  }
  
  return null;
};

/**
 * Generates an array of moment objects for a given date range.
 * @param {moment.Moment|Date|string} startDate - Start date of the range
 * @param {moment.Moment|Date|string} endDate - End date of the range
 * @returns {moment.Moment[]} Array of moment objects, oldest first
 */
export const generateDaysInRange = (startDate, endDate) => {
  const days = [];
  const start = moment(startDate).startOf('day');
  const end = moment(endDate).startOf('day');
  
  // Ensure we don't go into the future
  const today = moment().startOf('day');
  const effectiveEnd = end.isAfter(today) ? today : end;
  
  const current = moment(start);
  while (current.isSameOrBefore(effectiveEnd)) {
    days.push(moment(current));
    current.add(1, 'days');
  }
  
  return days;
};

/**
 * Generates an array of moment objects for the last 30 days ending today.
 * @deprecated Use generateDaysInRange instead for date range support
 * @returns {moment.Moment[]} Array of 30 moment objects, oldest first
 */
export const generateLast30Days = () => {
  const days = [];
  const today = moment().startOf('day');
  
  for (let i = 29; i >= 0; i--) {
    days.push(moment(today).subtract(i, 'days'));
  }
  
  return days;
};

/**
 * Aggregates connectivity events by day, filtering for "connected" type only.
 * Tracks connection types (WIFI/BLE) for each day.
 * @param {Array} events - Array of connectivity events with type, updateAt, and principalID fields
 * @returns {Map<string, {isConnected: boolean, connectionTypes: Set<string>}>} Map of date strings to connection info
 */
export const aggregateConnectivityByDay = (events) => {
  const connectedDays = new Map();
  
  if (!events || !Array.isArray(events)) {
    return connectedDays;
  }
  
  events.forEach((event) => {
    // Skip null entries
    if (event == null) {
      return;
    }
    
    // Only count "connected" type events
    if (event.type !== 'connected') {
      return;
    }
    
    // Use updateAt field to determine the date
    const eventDate = event.updateAt || event.updatedAt;
    if (!eventDate) {
      return;
    }
    
    const dateKey = moment(eventDate).format('YYYY-MM-DD');
    const connectionType = getConnectionTypeFromPrincipalID(event.principalID);
    
    if (!connectedDays.has(dateKey)) {
      connectedDays.set(dateKey, {
        isConnected: true,
        connectionTypes: new Set(),
      });
    }
    
    const dayData = connectedDays.get(dateKey);
    if (connectionType) {
      dayData.connectionTypes.add(connectionType);
    }
  });
  
  return connectedDays;
};

/**
 * Determines the effective connection type for display based on connection types for a day.
 * @param {Set<string>} connectionTypes - Set of connection types for the day
 * @returns {string} CONNECTION_TYPES value (WIFI, BLE, BOTH, or NONE)
 */
export const getEffectiveConnectionType = (connectionTypes) => {
  if (!connectionTypes || connectionTypes.size === 0) {
    return CONNECTION_TYPES.NONE;
  }
  if (connectionTypes.size > 1) {
    return CONNECTION_TYPES.BOTH;
  }
  return connectionTypes.values().next().value;
};

/**
 * Builds presence data array mapping each day to connection status and type.
 * @param {moment.Moment[]} days - Array of moment objects for each day
 * @param {Map<string, {isConnected: boolean, connectionTypes: Set<string>}>} aggregatedEvents - Map of date strings to connection info
 * @returns {Array<{date: moment.Moment, dateString: string, isConnected: boolean, connectionType: string}>}
 */
export const buildPresenceData = (days, aggregatedEvents) => {
  return days.map((day) => {
    const dateString = day.format('YYYY-MM-DD');
    const dayData = aggregatedEvents.get(dateString);
    
    return {
      date: day,
      dateString,
      isConnected: dayData?.isConnected || false,
      connectionType: dayData ? getEffectiveConnectionType(dayData.connectionTypes) : CONNECTION_TYPES.NONE,
    };
  });
};

/**
 * Convenience function to generate full presence data from events.
 * @param {Array} events - Array of connectivity events
 * @param {moment.Moment|Date|string} startDate - Start date of the range
 * @param {moment.Moment|Date|string} endDate - End date of the range
 * @returns {Array<{date: moment.Moment, dateString: string, isConnected: boolean, connectionType: string}>}
 */
export const getPresenceDataFromEvents = (events, startDate, endDate) => {
  const days = generateDaysInRange(startDate, endDate);
  const aggregatedEvents = aggregateConnectivityByDay(events);
  return buildPresenceData(days, aggregatedEvents);
};

export default {
  CONNECTION_TYPES,
  getConnectionTypeFromPrincipalID,
  getEffectiveConnectionType,
  generateLast30Days,
  generateDaysInRange,
  aggregateConnectivityByDay,
  buildPresenceData,
  getPresenceDataFromEvents,
};
